import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  fetchDonationEntries,
  addDonationEntry,
  deleteDonationEntry,
  type Donation,
  type DonationEntry,
} from '@/services/donations';

interface DonationEntriesModalProps {
  donation: Donation;
  onClose: () => void;
  onSaved: () => void;
}

export default function DonationEntriesModal({ donation, onClose, onSaved }: DonationEntriesModalProps) {
  const [entries, setEntries] = useState<DonationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [donatedAt, setDonatedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    const list = await fetchDonationEntries(donation.id);
    setEntries(list);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donation.id]);

  const total = entries.reduce((s, e) => s + e.amount, 0);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    setError('');
    if ((Number(amount) || 0) <= 0) {
      setError('El monto debe ser mayor a cero.');
      return;
    }
    setSaving(true);
    const { error: addError } = await addDonationEntry({
      donationId: donation.id,
      amount: Number(amount) || 0,
      donatedAt: donatedAt || '',
      notes,
    });
    setSaving(false);
    if (addError) {
      setError(addError);
      return;
    }
    setAmount('');
    setNotes('');
    await load();
    onSaved();
  }

  async function handleDelete(id: string) {
    const { error: delError } = await deleteDonationEntry(id, donation.id);
    if (!delError) {
      await load();
      onSaved();
    }
  }

  function formatDate(value: string) {
    if (!value) return '—';
    return new Date(value + 'T00:00:00').toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <Modal
      title="Historial de donaciones"
      subtitle={`${donation.periodName || donation.beneficiaryName} · Total: $${total.toLocaleString()}`}
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      <div className="space-y-4">
        <form onSubmit={handleAdd} className="space-y-3 bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-foreground-700">Registrar un valor donado</p>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Monto ($) *</label>
              <input type="number" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input type="date" className={inputCls} value={donatedAt} onChange={(e) => setDonatedAt(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Nota</label>
            <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Referencia, campaña, etc." maxLength={200} />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? 'Guardando...' : 'Agregar'}
            </button>
          </div>
        </form>

        <div>
          <p className="text-xs font-semibold text-foreground-700 mb-2">Historial ({entries.length})</p>
          {loading ? (
            <div className="text-center py-6 text-foreground-400 text-sm">
              <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando...
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-6 text-foreground-400 text-sm">
              Aún no hay valores donados registrados.
            </div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {entries.map((e) => (
                <div key={e.id} className="flex items-center gap-3 bg-white border border-background-200 rounded-lg px-3 py-2.5">
                  <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-hand-heart-line text-accent-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900">${e.amount.toLocaleString()}</p>
                    <p className="text-xs text-foreground-500 truncate">
                      {formatDate(e.donatedAt)}
                      {e.notes ? ` · ${e.notes}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(e.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                    aria-label="Eliminar"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}